import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Bliq-facing confirm page (English only). The Bliq manager opens the link from
// the WiseClean transport-request email and resolves each route one by one:
// for every pending route they enter the vehicle registration and either
// confirm or decline it. Confirming a route marked "to client" triggers the
// client demo-ride invite once (handled in ws5).
const API = 'https://api.wiseclean.ee/api:wc-bliq';

const C = {
  bg: '#E8F7FA',
  card: '#FFFFFF',
  cyan: '#6DCBDE',
  cyanDark: '#1A7D8E',
  text: '#1A1A2E',
  muted: '#6B7280',
  border: '#E2E8F0',
  error: '#EF4444',
  success: '#16A34A',
};

const S = {
  page: { minHeight: '100vh', background: C.bg, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '24px', fontFamily: 'DM Sans, Arial, sans-serif', color: C.text },
  card: { background: C.card, borderRadius: 16, padding: '28px 24px', maxWidth: 460, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
  h1: { fontSize: 20, fontWeight: 700, margin: '0 0 16px' },
  row: { display: 'flex', gap: 8, fontSize: 14, padding: '6px 0', borderBottom: `1px solid ${C.border}` },
  label: { color: C.muted, minWidth: 64 },
  routeCard: { border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px', marginTop: 12 },
  routeHead: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, fontSize: 15, fontWeight: 600 },
  routeMeta: { color: C.muted, fontSize: 13, marginTop: 4 },
  badge: { display: 'inline-block', fontSize: 12, fontWeight: 600, color: C.cyanDark, background: C.bg, borderRadius: 20, padding: '2px 10px' },
  input: { width: '100%', boxSizing: 'border-box', fontSize: 16, padding: '12px', borderRadius: 8, border: `1px solid ${C.border}`, marginTop: 6 },
  btn: { width: '100%', padding: '13px', borderRadius: 8, border: 'none', background: C.cyan, color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer', marginTop: 12 },
  btnGhost: { width: '100%', padding: '13px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: 16, fontWeight: 600, cursor: 'pointer', marginTop: 8 },
  btnDisabled: { opacity: 0.6, cursor: 'default' },
  statusConfirmed: { color: C.success, fontSize: 14, fontWeight: 600, marginTop: 8 },
  statusDeclined: { color: C.error, fontSize: 14, fontWeight: 600, marginTop: 8 },
  muted: { color: C.muted, fontSize: 14 },
  err: { color: C.error, fontSize: 14, marginTop: 10 },
};

export default function BliqConfirm() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [state, setState] = useState('loading'); // loading|form|done|error|expired
  const [req, setReq] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [regs, setRegs] = useState([]);
  const [regByIdx, setRegByIdx] = useState({});
  const [submittingIdx, setSubmittingIdx] = useState(null);
  const [errByIdx, setErrByIdx] = useState({});
  const inFlight = useRef(false);

  useEffect(() => {
    if (!token) { setState('error'); return; }
    let cancelled = false;
    fetch(`${API}/request_by_token?confirm_token=${encodeURIComponent(token)}`)
      .then(async (r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then((d) => {
        if (cancelled) return;
        if (d.expired) { setState('expired'); return; }
        setReq(d);
        const list = Array.isArray(d.routes) ? d.routes : [];
        setRoutes(list);
        const initial = {};
        list.forEach((rt) => { if (rt.vehicle_reg) initial[rt.idx] = rt.vehicle_reg; });
        setRegByIdx(initial);
        setState('form');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    fetch(`${API}/vehicle_regs?confirm_token=${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => { if (!cancelled && Array.isArray(list)) setRegs(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  function setReg(idx, value) {
    setRegByIdx((prev) => ({ ...prev, [idx]: value }));
  }

  function setRouteErr(idx, message) {
    setErrByIdx((prev) => ({ ...prev, [idx]: message }));
  }

  async function resolve(idx, decision) {
    if (inFlight.current || submittingIdx !== null) return;
    const entered = (regByIdx[idx] || '').trim();
    if (decision === 'confirm' && !entered) {
      setRouteErr(idx, t('bliqConfirmRegRequired'));
      return;
    }
    inFlight.current = true;
    setSubmittingIdx(idx);
    setRouteErr(idx, '');
    try {
      const body = { confirm_token: token, route_idx: idx, decision };
      if (decision === 'confirm') body.vehicle_reg = entered;
      const r = await fetch(`${API}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => null);
        throw new Error((b && b.message) || t('bliqConfirmFailed'));
      }
      const resp = await r.json().catch(() => null);
      if (resp && Array.isArray(resp.routes)) {
        setRoutes(resp.routes);
      } else {
        setRoutes((prev) => prev.map((rt) => (
          rt.idx === idx ? { ...rt, status: decision === 'confirm' ? 'confirmed' : 'declined', vehicle_reg: decision === 'confirm' ? entered : rt.vehicle_reg } : rt
        )));
      }
    } catch (e) {
      setRouteErr(idx, e.message);
    } finally {
      inFlight.current = false;
      setSubmittingIdx(null);
    }
  }

  function statusWord(status) {
    if (status === 'confirmed') return t('bliqConfirmStatusConfirmed');
    if (status === 'declined') return t('bliqConfirmStatusDeclined');
    return t('bliqConfirmStatusPending');
  }

  const allResolved = routes.length > 0 && routes.every((rt) => rt.status !== 'pending');

  let content;
  if (state === 'loading') content = <p style={S.muted}>{t('bliqConfirmLoading')}</p>;
  else if (state === 'error') content = (<><h1 style={S.h1}>{t('bliqConfirmErrorTitle')}</h1><p style={S.muted}>{t('bliqConfirmErrorBody')}</p></>);
  else if (state === 'expired') content = (<><h1 style={S.h1}>{t('bliqConfirmExpiredTitle')}</h1><p style={S.muted}>{t('bliqConfirmExpiredBody')}</p></>);
  else content = (
    <>
      <h1 style={S.h1}>{t('bliqConfirmTitle')}</h1>
      <p style={S.muted}>{t('bliqConfirmIntro')}</p>
      <div style={{ margin: '8px 0 4px' }}>
        <div style={{ ...S.row, borderBottom: 'none' }}><span style={S.label}>{t('bliqConfirmDate')}</span><span>{(req && req.order_date) || '-'}</span></div>
      </div>
      <datalist id="bliq-regs">
        {regs.map((rg) => <option key={rg} value={rg} />)}
      </datalist>
      {routes.map((rt) => {
        const pending = rt.status === 'pending';
        const busy = submittingIdx === rt.idx;
        const routeErr = errByIdx[rt.idx];
        return (
          <div key={rt.idx} style={S.routeCard}>
            <div style={S.routeHead}>
              <span>{rt.from || '-'} &rarr; {rt.to || '-'}</span>
              {rt.to_client && <span style={S.badge}>{t('bliqConfirmToClient')}</span>}
            </div>
            <div style={S.routeMeta}>
              {rt.time ? rt.time + ' · ' : ''}{t('bliqConfirmRoute')} {rt.idx + 1}
            </div>
            {pending ? (
              <>
                <label style={{ display: 'block', marginTop: 12 }}>
                  <span style={{ fontSize: 13, color: C.muted }}>{t('bliqConfirmRegLabel')}</span>
                  <input
                    style={S.input}
                    list="bliq-regs"
                    value={regByIdx[rt.idx] || ''}
                    onChange={(e) => setReg(rt.idx, e.target.value)}
                    placeholder="123 ABC"
                    disabled={busy}
                  />
                </label>
                {routeErr && <div style={S.err}>{routeErr}</div>}
                <button
                  style={{ ...S.btn, ...(submittingIdx !== null ? S.btnDisabled : {}) }}
                  onClick={() => resolve(rt.idx, 'confirm')}
                  disabled={submittingIdx !== null}
                >
                  {busy ? t('bliqConfirmConfirming') : t('bliqConfirmConfirm')}
                </button>
                <button
                  style={{ ...S.btnGhost, ...(submittingIdx !== null ? S.btnDisabled : {}) }}
                  onClick={() => resolve(rt.idx, 'decline')}
                  disabled={submittingIdx !== null}
                >
                  {t('bliqConfirmDecline')}
                </button>
              </>
            ) : (
              <div style={rt.status === 'declined' ? S.statusDeclined : S.statusConfirmed}>
                {statusWord(rt.status)}{rt.status === 'confirmed' && rt.vehicle_reg ? ` · ${rt.vehicle_reg}` : ''}
              </div>
            )}
          </div>
        );
      })}
      {routes.length === 0 && <p style={S.muted}>{t('bliqConfirmNoRoutes')}</p>}
      {allResolved && (
        <div style={{ ...S.statusConfirmed, marginTop: 16, fontSize: 15 }}>{t('bliqConfirmAllSet')}</div>
      )}
    </>
  );

  return (
    <div style={S.page}>
      <div style={S.card}>{content}</div>
    </div>
  );
}
